// ------------------------------------------------------------------------------
// <copyright file="manualQaRoutePolicy.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

const C2HL_TENANT_ID = 'c2hl';
const ADMIN_ROLE = 'Admin';
const SUPER_ADMIN_ROLE = 'SuperAdmin';
const PLATFORM_CONTRIBUTOR_ROLE = 'PlatformContributor';
const MEMBER_ROLE = 'Member';

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1']);

function readWindowHostname() {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location?.hostname || '';
}

function readManualQaRequireAuthFlag() {
  return process.env.REACT_APP_MANUAL_QA_REQUIRE_AUTH || '';
}

function readManualQaLocalPreviewFlag() {
  return process.env.REACT_APP_MANUAL_QA_LOCAL_PREVIEW || '';
}

function isTrue(value) {
  return String(value || '').toLowerCase() === 'true';
}

export function shouldBypassManualQaAuth(options = {}) {
  const hostname = options.hostname ?? readWindowHostname();
  const requireAuth = options.requireAuth ?? readManualQaRequireAuthFlag();
  const localPreview = options.localPreview ?? readManualQaLocalPreviewFlag();

  return LOCAL_HOSTNAMES.has(hostname)
    && !isTrue(requireAuth)
    && isTrue(localPreview);
}

export function getManualQaRoutePolicy(routeKind, options = {}) {
  return {
    bypassAuth: shouldBypassManualQaAuth(options),
    requiredRole: routeKind === 'admin' ? ADMIN_ROLE : undefined
  };
}

export function canAccessManualQaAdmin(user) {
  const role = user?.Role || user?.role || '';
  const tenantId = user?.TenantId || user?.tenantId || '';

  if (user?.IsPlatformAdmin || user?.isPlatformAdmin) {
    return true;
  }

  if (role === SUPER_ADMIN_ROLE) {
    return true;
  }

  return tenantId === C2HL_TENANT_ID
    && (role === ADMIN_ROLE || role === PLATFORM_CONTRIBUTOR_ROLE);
}

/**
 * Whether `user` is eligible to see/use the Manual QA tester runner
 * (the `/manual-qa` route and its landing-page nav link).
 *
 * The route itself (`ManualQaRoute` in App-revised.js) accepts any
 * authenticated user — testers are authorized by campaign assignment on the
 * backend (`ManualQaAuthorizationService`), not by a global role, so the
 * route intentionally sets no `requiredRole`. `ManualQaTesterProvisioningService`
 * provisions C2HL-owned tester logins with `TenantId = "c2hl"` and
 * `Role = "Member"` — that is the population this predicate recognizes,
 * in addition to everyone who can already reach the Test Admin workspace.
 * Scoping to `TenantId === "c2hl"` (mirroring `canAccessManualQaAdmin`)
 * keeps this from surfacing the internal QA tool to a regular `Member` in
 * any other tenant.
 */
export function canAccessManualQaRunner(user) {
  if (canAccessManualQaAdmin(user)) {
    return true;
  }

  const role = user?.Role || user?.role || '';
  const tenantId = user?.TenantId || user?.tenantId || '';

  return tenantId === C2HL_TENANT_ID && role === MEMBER_ROLE;
}

/**
 * Whether `user` is a Manual QA tester with NO other AdminPortal access — i.e. eligible for
 * the `/manual-qa` runner but not the Test Admin workspace or any internal admin surface.
 *
 * Provisioned testers are C2HL `Member` accounts (`ManualQaTesterProvisioningService`). They are
 * intended to reach only the tester runner; every other AdminPortal route (`/`, `/host`,
 * `/glossary`, `/admin-settings`, …) should default-deny them and send them to `/manual-qa`.
 * This predicate is the default-deny signal used by `ProtectedRoute`.
 */
export function isManualQaTesterOnly(user) {
  return canAccessManualQaRunner(user) && !canAccessManualQaAdmin(user);
}
